import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { logWarn } from "@/lib/log";

const AuthContext = createContext(null);

/**
 * Auth is driven entirely by an httpOnly `tf_auth` cookie set by the backend.
 * The token is never accessible to JavaScript — no localStorage, no Authorization
 * header from the client. This eliminates the XSS token-theft vector.
 * The cookie is sent automatically on every request thanks to `withCredentials`.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch (err) {
      // 401 = not signed in (or cookie expired). Anything else = network / server.
      if (err?.response?.status && err.response.status !== 401) {
        logWarn("[auth] /auth/me error", err.response.status);
      }
      setUser(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refreshMe(); }, [refreshMe]);

  const login = useCallback(async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); }
    catch (err) { logWarn("[auth] logout failed", err?.response?.status); }
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refreshMe }),
    [user, loading, login, register, logout, refreshMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
